import {parse, stringify} from 'qs';
import Url from 'url-parse';
import {CashChargeCode} from '../../../domain/buy/cash/type';
import {APP_DONWLOAD_PAGE_TRY_SCHEME_PARAM_KEY} from '../constants';
import {IS_CLIENT_SIDE} from '../utils/constants';
import {GoogleAtuError} from './GoogleAtuError.class';
import {InvalidAppSchemeError} from './InvalidAppSchemeError.class';
import {MalformedAppSchemeError} from './MalformedAppSchemeError.class';
import {PagewebUrl} from './PagewebUrl.class';
/**
 * 페이지 앱 URL스킴을 파싱해 해당되는 웹 URL을 반환하는 함수입니다.
 *
 * 아래 문서들에 정의된 스킴 리스트를 참고해서 작성했습니다.
 * https://docs.google.com/spreadsheets/d/1XrJKTTq13mpP16W55qqD4im1W4K0S2vE1V7616oMUDs
 * https://podotree.atlassian.net/wiki/spaces/kakaopagedev/pages/2715682200/Next
 */
export function getPagewebUrl({
  appScheme,
  origin,
}: {
  appScheme: string;
  origin?: string;
}): PagewebUrl | string {
  const {
    protocol,
    hostname,
    pathname: _pathname,
    query: _query,
  } = new Url(appScheme);
  const actionGroup = hostname;
  const action = _pathname.replace(/(^\/|\/$)/g, '');
  const splittedActionList = action.split('/');
  const querystring = _query.replace(/^\?/g, '');
  const parsedQueryparam = parse(querystring);

  if (protocol !== 'kakaopage:') {
    throw new InvalidAppSchemeError(appScheme);
  }

  let path;
  let queryparam;
  let openNewWindow = false;

  if (actionGroup === 'open') {
    if (splittedActionList.length === 0) {
      throw new MalformedAppSchemeError();
    }
    switch (splittedActionList[0]) {
      case 'google_auto':
      case 'google_auto_active': {
        throw new GoogleAtuError();
      }
      case 'main':
        {
          path = '/';
          queryparam = parsedQueryparam;
        }
        break;
      case 'subtab':
        {
          const {screen_uid, menu_uid, ...rest} = parsedQueryparam;
          if (!menu_uid) {
            throw new MalformedAppSchemeError();
          }
          const screenUid = (screen_uid as string) || '';
          const menuUid = (menu_uid as string) || '';

          path = `/menu/${menuUid}${screenUid ? `/screen/${screenUid}` : ''}`;
          queryparam = {...rest};
        }
        break;
      case 'content':
        {
          if (splittedActionList.length < 2) {
            const {series_id, ...rest} = parsedQueryparam;
            if (!series_id) {
              throw new MalformedAppSchemeError();
            }
            const seriesId = (series_id as string) || '';

            path = `/content${seriesId ? `/${seriesId}` : ''}`;
            queryparam = rest;
            break;
          }

          const subaction = splittedActionList[1];
          switch (subaction) {
            case 'set_overview': {
              const {set_id, ...rest} = parsedQueryparam;
              if (!set_id) {
                throw new MalformedAppSchemeError();
              }
              path = `/content/set-overview/${set_id}`;
              queryparam = rest;
              break;
            }
            default:
              throw new MalformedAppSchemeError();
          }
        }
        break;
      case 'viewer':
        {
          const {product_id, series_id, ...rest} = parsedQueryparam;
          if (!product_id || !series_id) {
            throw new MalformedAppSchemeError();
          }
          const seriesId = (series_id as string) || '';
          const productId = (product_id as string) || '';

          path = `/content/${seriesId}/viewer/${productId}`;
          queryparam = {...rest};
        }
        break;
      case 'inven':
        {
          if (splittedActionList.length > 2 || splittedActionList.length < 2) {
            throw new MalformedAppSchemeError();
          }
          const subaction = splittedActionList[1];
          switch (subaction) {
            case 'recent':
              {
                path = '/inven/recent';
                queryparam = parsedQueryparam;
              }
              break;
            case 'like':
              {
                path = '/inven/like';
                queryparam = parsedQueryparam;
              }
              break;
            case 'paid':
              {
                path = '/inven/paid';
                queryparam = parsedQueryparam;
              }
              break;
            case 'download':
            default: {
              path = '/appdownload';
              queryparam = {
                [APP_DONWLOAD_PAGE_TRY_SCHEME_PARAM_KEY]:
                  encodeURIComponent(appScheme),
              };
            }
          }
        }
        break;
      case 'buy_cash': {
        path = '/buy/cash';
        queryparam = parsedQueryparam;
        if (!Object.keys(queryparam).length) {
          queryparam['code'] = String(CashChargeCode.Start);
          queryparam['returnUrl'] = IS_CLIENT_SIDE ? window.location.href : '';
        }
        openNewWindow = true;
        break;
      }
      case 'webview':
        {
          if (splittedActionList.length > 1) {
            const [_, subaction, ...extraPaths] = splittedActionList;
            if (subaction === 'event') {
              const {hash_uid} = parsedQueryparam;
              if (!hash_uid) {
                throw new MalformedAppSchemeError();
              }
            } else if (subaction === 'gacha') {
              if (!(parsedQueryparam?.gacha_uid || parsedQueryparam?.map_uid)) {
                throw new MalformedAppSchemeError();
              }
            } else if (subaction === 'policy') {
              if (splittedActionList.length > 2) {
                const policyType = splittedActionList[2];
                if (!policyType) {
                  throw new MalformedAppSchemeError();
                }
              } else {
                throw new MalformedAppSchemeError();
              }
            } else if (subaction === 'autocharge') {
              openNewWindow = true;
              return new PagewebUrl({
                origin,
                path: '/relay/autocharge',
                queryparam: parsedQueryparam,
                openNewWindow,
              });
            } else if (subaction === 'tvpoint') {
              openNewWindow = true;
              return new PagewebUrl({
                origin,
                path: '/relay/tvpoint',
                queryparam: parsedQueryparam,
                openNewWindow,
              });
            } else if (subaction === 'ask') {
              return new PagewebUrl({
                origin,
                path: '/ask',
                queryparam: parsedQueryparam,
                openNewWindow,
              });
            } else if (subaction === 'credit_refund') {
              if (extraPaths.length > 1 || extraPaths[0] !== 'agreement') {
                throw new MalformedAppSchemeError();
              }
              return new PagewebUrl({
                origin,
                path: '/credit-refund/agreement',
                queryparam: parsedQueryparam,
                openNewWindow,
              });
            }
          }

          path = `/${hostname}/${action}`;
          queryparam = parsedQueryparam;
        }
        break;
      case 'app':
        {
          if (
            action !== 'app/store' &&
            action !== 'app/update' &&
            action !== 'app/settings'
          ) {
            throw new MalformedAppSchemeError();
          }
          path = '/appdownload';
          queryparam = parsedQueryparam;
        }
        break;
      case 'landing':
        {
          const subaction = splittedActionList[1];
          switch (subaction) {
            case 'series':
              {
                if (
                  splittedActionList.length > 3 ||
                  splittedActionList.length < 3
                ) {
                  throw new MalformedAppSchemeError();
                }
                const {
                  reference: _reference,
                  theme_keyword_uid,
                  ...rest
                } = parsedQueryparam;
                if (!_reference) {
                  throw new MalformedAppSchemeError();
                }
                const reference = _reference as string;
                const viewType = splittedActionList[2];

                switch (viewType) {
                  case 'list':
                    path = `/landing/series/list/${reference}${
                      theme_keyword_uid ? `/theme/${theme_keyword_uid}` : ''
                    }`;
                    queryparam = rest;
                    break;
                  case 'poster':
                    path = `/landing/series/poster/${reference}${
                      theme_keyword_uid ? `/theme/${theme_keyword_uid}` : ''
                    }`;
                    queryparam = rest;
                    break;
                  case 'card':
                    path = `/landing/series/card/${reference}${
                      theme_keyword_uid ? `/theme/${theme_keyword_uid}` : ''
                    }`;
                    queryparam = rest;
                    break;
                  default: {
                    throw new MalformedAppSchemeError();
                  }
                }
              }
              break;
            case 'single':
              {
                if (
                  splittedActionList.length > 3 ||
                  splittedActionList.length < 3
                ) {
                  throw new MalformedAppSchemeError();
                }
                const {
                  reference: _reference,
                  theme_keyword_uid,
                  ...rest
                } = parsedQueryparam;
                if (!_reference) {
                  throw new MalformedAppSchemeError();
                }
                const reference = _reference as string;

                const viewType = splittedActionList[2];
                if (viewType !== 'list') {
                  throw new MalformedAppSchemeError();
                }
                path = `/landing/single/list/${reference}${
                  theme_keyword_uid ? `/theme/${theme_keyword_uid}` : ''
                }`;
                queryparam = rest;
              }
              break;
            case 'set':
              {
                if (
                  splittedActionList.length > 3 ||
                  splittedActionList.length < 3
                ) {
                  throw new MalformedAppSchemeError();
                }
                const {reference: _reference, ...rest} = parsedQueryparam;
                if (!_reference) {
                  throw new MalformedAppSchemeError();
                }
                const reference = _reference as string;
                const viewType = splittedActionList[2];

                switch (viewType) {
                  case 'list':
                    path = `/landing/set/list/${reference}`;
                    queryparam = rest;
                    break;
                  case 'poster':
                    path = `/landing/set/poster/${reference}`;
                    queryparam = rest;
                    break;
                  default: {
                    throw new MalformedAppSchemeError();
                  }
                }
              }
              break;
            case 'today_new':
              {
                if (
                  splittedActionList.length > 2 ||
                  splittedActionList.length < 2
                ) {
                  throw new MalformedAppSchemeError();
                }
                const {category_uid, subcategory_uid, ...rest} =
                  parsedQueryparam;
                if (!category_uid) {
                  throw new MalformedAppSchemeError();
                }
                const categoryUid = category_uid as string;
                const subcategoryUid = (subcategory_uid as string) || '';

                path = `/landing/today-new/${categoryUid}${
                  subcategoryUid ? `/${subcategoryUid}` : ''
                }`;
                queryparam = rest;
              }
              break;
            case 'today_up':
              {
                if (
                  splittedActionList.length > 2 ||
                  splittedActionList.length < 2
                ) {
                  throw new MalformedAppSchemeError();
                }
                const {category_uid, subcategory_uid, ...rest} =
                  parsedQueryparam;
                if (!category_uid) {
                  throw new MalformedAppSchemeError();
                }
                const categoryUid = category_uid as string;
                const subcategoryUid = (subcategory_uid as string) || '';

                path = `/landing/today-up/${categoryUid}${
                  subcategoryUid ? `/${subcategoryUid}` : ''
                }`;
                queryparam = rest;
              }
              break;
            case 'original':
              {
                if (
                  splittedActionList.length > 2 ||
                  splittedActionList.length < 2
                ) {
                  throw new MalformedAppSchemeError();
                }
                const {category_uid, subcategory_uid, ...rest} =
                  parsedQueryparam;
                if (!category_uid) {
                  throw new MalformedAppSchemeError();
                }
                const categoryUid = category_uid as string;
                const subcategoryUid = (subcategory_uid as string) || '';

                path = `/landing/original/${categoryUid}${
                  subcategoryUid ? `/${subcategoryUid}` : ''
                }`;
                queryparam = rest;
              }
              break;
            case 'event':
              {
                if (
                  splittedActionList.length > 2 ||
                  splittedActionList.length < 2
                ) {
                  throw new MalformedAppSchemeError();
                }
                const {category_uid, ...rest} = parsedQueryparam;
                const categoryUid = category_uid as string;

                path = `/landing/event${categoryUid ? `/${categoryUid}` : ''}`;
                queryparam = rest;
              }
              break;
            case 'ranking':
              {
                if (
                  splittedActionList.length > 2 ||
                  splittedActionList.length < 2
                ) {
                  throw new MalformedAppSchemeError();
                }
                const {category_uid, subcategory_uid, ...rest} =
                  parsedQueryparam;
                if (!category_uid) {
                  throw new MalformedAppSchemeError();
                }
                const categoryUid = category_uid as string;
                const subcategoryUid = (subcategory_uid as string) || '';

                path = `/landing/ranking/${categoryUid}${
                  subcategoryUid ? `/${subcategoryUid}` : ''
                }`;
                queryparam = rest;
              }
              break;
            case 'genre':
              {
                if (
                  splittedActionList.length > 2 ||
                  splittedActionList.length < 2
                ) {
                  throw new MalformedAppSchemeError();
                }
                const {category_uid, subcategory_uid, sort_type, ...rest} =
                  parsedQueryparam;
                if (!category_uid) {
                  throw new MalformedAppSchemeError();
                }
                const categoryUid = category_uid as string;
                const subcategoryUid = (subcategory_uid as string) || '';

                path = `/landing/genre/${categoryUid}${
                  subcategoryUid ? `/${subcategoryUid}` : ''
                }`;
                if (sort_type) {
                  rest.sort_opt = sort_type;
                }
                queryparam = rest;
              }
              break;
            case 'dayofweek':
              {
                const {
                  category_uid,
                  subcategory_uid,
                  screen_uid,
                  menu_uid,
                  ...rest
                } = parsedQueryparam;
                const categoryUid = (category_uid as string) || '';
                const subcategoryUid = (subcategory_uid as string) || '';
                const screenUid = (screen_uid as string) || '';
                const menuUid = (menu_uid as string) || '';

                if (screenUid && menuUid) {
                  path = `/menu/${menuUid}/screen/${screenUid}`;
                  queryparam = {
                    ...rest,
                    ...(categoryUid && {category_uid: categoryUid}),
                    ...(subcategoryUid && {subcategory_uid: subcategoryUid}),
                  };
                } else if (categoryUid) {
                  path = `/landing/day-of-week/${categoryUid}${
                    subcategoryUid ? `/${subcategoryUid}` : ''
                  }`;
                  queryparam = rest;
                } else {
                  throw new MalformedAppSchemeError();
                }
              }
              break;
            case 'freeseries':
              {
                const {
                  category_uid,
                  subcategory_uid,
                  screen_uid,
                  menu_uid,
                  ...rest
                } = parsedQueryparam;
                const categoryUid = (category_uid as string) || '';
                const subcategoryUid = (subcategory_uid as string) || '';
                const screenUid = (screen_uid as string) || '';
                const menuUid = (menu_uid as string) || '';

                if (screenUid && menuUid) {
                  path = `/menu/${menuUid}/screen/${screenUid}`;
                  queryparam = {
                    ...rest,
                    ...(categoryUid && {category_uid: categoryUid}),
                    ...(subcategoryUid && {subcategory_uid: subcategoryUid}),
                  };
                } else if (categoryUid) {
                  path = `/landing/free-series/${categoryUid}${
                    subcategoryUid ? `/${subcategoryUid}` : ''
                  }`;
                  queryparam = rest;
                } else {
                  throw new MalformedAppSchemeError();
                }
              }
              break;
            default: {
              throw new MalformedAppSchemeError();
            }
          }
        }
        break;
      case 'browser': {
        const {url, ...rest} = parsedQueryparam;
        if (!url) {
          throw new MalformedAppSchemeError();
        }
        const querystring =
          Object.keys(rest).length > 0 ? `?${stringify(rest)}` : '';

        return `${url as string}${querystring}`;
      }
      case 'today_gift_list':
        {
          const {category_uid, ...rest} = parsedQueryparam;
          const categoryUid = category_uid as string;

          path = `/today-gift${categoryUid ? `/${categoryUid}` : '/0'}`;
          queryparam = rest;
        }
        break;
      case 'comments':
        {
          const {series_id, comment_uid, ...rest} = parsedQueryparam;
          if (!series_id) {
            throw new MalformedAppSchemeError();
          }
          if (comment_uid) {
            path = '/comment/detail';
          } else {
            path = '/comment';
          }
          queryparam = {
            seriesId: series_id,
            commentUid: comment_uid,
            ...rest,
          };
        }
        break;
      /** @deprecated */
      case 'keytalk':
        {
          if (splittedActionList.length > 2 || splittedActionList.length < 2) {
            throw new MalformedAppSchemeError();
          }
          const subaction = splittedActionList[1];
          switch (subaction) {
            case 'search':
              {
                const {category_uid, ...rest} = parsedQueryparam;
                if (!category_uid) {
                  throw new MalformedAppSchemeError();
                }
                const categoryUid = category_uid as string;

                path = `/keytalk-search/${categoryUid}`;
                queryparam = rest;
              }
              break;
            default: {
              throw new MalformedAppSchemeError();
            }
          }
        }
        break;
      case 'cash_sponsor':
        {
          const {event_uid, ...rest} = parsedQueryparam;
          if (!event_uid) {
            throw new MalformedAppSchemeError();
          }
          const eventUid = (event_uid as string) || '';

          path = `/cash-sponsor/${eventUid}`;
          queryparam = rest;
        }
        break;
      case 'search':
        {
          if (splittedActionList.length > 2 || splittedActionList.length < 2) {
            throw new MalformedAppSchemeError();
          }
          const subaction = splittedActionList[1];
          switch (subaction) {
            case 'main':
              {
                path = '/search';
                queryparam = parsedQueryparam;
              }
              break;
            case 'series':
              {
                const {text, ...rest} = parsedQueryparam;
                if (!text) {
                  throw new MalformedAppSchemeError();
                }
                const keyword = (text as string) || '';

                path = '/search/series';
                queryparam = {...rest, keyword};
              }
              break;
            case 'filter':
              {
                const {filter_list, ...rest} = parsedQueryparam;
                if (!filter_list) {
                  throw new MalformedAppSchemeError();
                }
                const filterListString = filter_list as string;

                path = '/search/themekeyword';
                queryparam = {...rest, filterList: filterListString};
              }
              break;
            /** @deprecated */
            case 'keytalk':
              {
                const {category_uid, keytalk_list, ...rest} = parsedQueryparam;
                if (!category_uid || !keytalk_list) {
                  throw new MalformedAppSchemeError();
                }
                const categoryUid = category_uid as string;
                const keytalkListString = keytalk_list as string;

                path = '/search/keytalk';
                queryparam = {
                  ...rest,
                  category_uid: categoryUid,
                  filter: keytalkListString,
                };
              }
              break;
            default: {
              throw new MalformedAppSchemeError();
            }
          }
        }
        break;
      case 'mynews':
        {
          const {tab, ...rest} = parsedQueryparam;

          if (tab) {
            switch (tab) {
              case 'MY':
                {
                  path = '/mynews/my';
                  queryparam = {...rest};
                }
                break;
              case 'UP':
                {
                  path = '/mynews/up';
                  queryparam = {...rest};
                }
                break;
              case 'WAITFREE':
                {
                  path = '/appdownload';
                  queryparam = {
                    [APP_DONWLOAD_PAGE_TRY_SCHEME_PARAM_KEY]:
                      encodeURIComponent(appScheme),
                  };
                }
                break;
              default: {
                throw new MalformedAppSchemeError();
              }
            }
          } else {
            path = '/mynews';
            queryparam = rest;
          }
        }
        break;
      // XXX: 캐시프렌즈는 추후 웹에서도 지원할 경우 수정해야 합니다.
      case 'cashfriends':
      case 'video_playground': {
        path = '/appdownload';
        queryparam = {
          [APP_DONWLOAD_PAGE_TRY_SCHEME_PARAM_KEY]:
            encodeURIComponent(appScheme),
        };
        break;
      }
      case 'ongoing':
        {
          path = '/day-of-week';
          queryparam = parsedQueryparam;
        }
        break;
      default: {
        // TODO: @steve-ca-kakaoent 웹에서 지원하지 않는 스킴의 경우 무조건 앱으로 보내면 될 것으로 보입니다. 추후 확인 및 수정이 필요합니다.
        throw new InvalidAppSchemeError(appScheme);
      }
    }
  } else {
    throw new MalformedAppSchemeError();
  }

  return new PagewebUrl({
    origin,
    path,
    queryparam,
    openNewWindow,
  });
}
